import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DataTable, { TableColumn } from 'react-data-table-component';
import { EmptyResult } from '../../../../../_metronic/helpers/components/EmptyResult';
import {
  encryptId,
  formatPrice,
  KTSVG,
} from '../../../../../_metronic/helpers';
import { colorStatusCuentasCobrar } from '../../../../../_metronic/helpers/CustomElements';

type Props = {
  cuentas: any;
  cargandoCuentas: boolean;
};

const ListadoTabla: React.FC<Props> = ({ cuentas, cargandoCuentas }) => {
  const [data, setData] = useState<any>([]);
  useEffect(() => {
    if (cuentas) {
      setData(cuentas);
    }
    return () => {
      setData([]);
    };
  }, [cuentas]);

  const columns: TableColumn<any>[] = [
    {
      name: '',
      selector: (row: any) => row.nombre,
      cell: (row: any) => (
        <>
          <span
            className="badge fs-7 fw-bold mr-15 min-w-150px"
            style={{
              color: '#fff',
              backgroundColor: row.color ? row.color : '#9096EC',
            }}
          >
            {row.nombre}
          </span>
          <div
            className="badge badge-light-primary fs-7 fw-bold mr-15"
            title="Total de tareas"
          >
            {row.items.length}
          </div>

          <div className="d-inline-flex gap-2">
            <span
              className="badge badge-light-success fs-7 fw-bold"
              title="Total de tareas"
            >
              {`${formatPrice(row.monto_total_mxn, 'es-MX', 'MXN', 2, 2)}`} MXN
            </span>
            <span
              className="badge badge-light-info fs-7 fw-bold"
              title="Total de tareas"
            >
              {`${formatPrice(row.monto_total_usd, 'es-MX', 'MXN', 2, 2)}`} USD
            </span>
          </div>
        </>
      ),
      sortable: false,
      width: '250px',
    },
  ];

  return (
    <>
      {cargandoCuentas ? (
        <>
          <EmptyResult title="" loading={cargandoCuentas} />
        </>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data}
            responsive
            expandableRows
            expandableRowsComponent={expandedComponent}
            noDataComponent={
              <>
                <div className="d-flex align-items-center">
                  <div className="text-muted text-center fs-6 w-100 m-5">
                    Sin registros
                  </div>
                </div>
              </>
            }
            onRowExpandToggled={(event) => {}}
          />
        </>
      )}
    </>
  );
};

const expandedComponent = (d: any) => {
  const columns_section: any = [
    {
      id: 'nombre',
      name: <div className="fw-bolder text-muted me-5 ">Orden de cobro</div>,
      selector: (row: any) => row.nombre,
      cell: (row: any) => (
        <>
          <Link to={`/cobranza/cuentas_cobrar/${encryptId(row.id.toString())}`}>
            {`${row.nombre}`}
          </Link>
        </>
      ),
      sortable: true,
    },
    {
      id: 'tipo_pago',
      name: <div className="fw-bolder text-muted me-5">Tipo de pago</div>,
      selector: (row: any) =>
        row.tipoPago ? row.tipoPago.name : 'No registrado',
      cell: (row: any) => (
        <>
          {row.tipoPago && (
            <>
              <div className="text-gray-700">{row.tipoPago.name}</div>
            </>
          )}
        </>
      ),
      sortable: true,
    },
    {
      id: 'asignado_a',
      name: <div className="fw-bolder text-muted me-5">Cobrador asignado</div>,
      selector: (row: any) =>
        row.cobrador
          ? `${row.cobrador.first_name} ${row.cobrador.father_last_name}`
          : 'No asignado',
      cell: (row: any) => (
        <>
          <div className="symbol symbol-30px tooltip-soft">
            {row.cobrador ? (
              <>
                {row.cobrador && row.cobrador.profile_photo ? (
                  <>
                    <img
                      src={`${process.env.REACT_APP_PUBLIC}users/${row?.cobrador.profile_photo}`}
                      crossOrigin="anonymous"
                      className="symbol-label fs-8 "
                      alt="user"
                    />
                    <span className="tooltiptext">{`${row.cobrador.first_name} ${row.cobrador.father_last_name} ${row.cobrador.mother_last_name}`}</span>
                  </>
                ) : (
                  <>
                    <span className="symbol-label fs-8 fw-bold bg-light-primary text-primary fw-bold">
                      {`${row.cobrador.first_name[0]}${row.cobrador.father_last_name[0]}`}
                    </span>
                    <span className="tooltiptext">{`${row.cobrador.first_name} ${row.cobrador.father_last_name} ${row.cobrador.mother_last_name}`}</span>
                  </>
                )}
              </>
            ) : (
              <>
                <span className="symbol symbol-30px cursor-pointer tooltip-soft">
                  <div className="symbol-label">
                    <KTSVG
                      path="/media/icons/duotune/communication/com006.svg"
                      className="svg-icon-1"
                    />
                  </div>
                  <span className="tooltiptext">No asignado</span>
                </span>
              </>
            )}
          </div>
        </>
      ),
      sortable: true,
      width: '190px',
    },
    {
      id: 'monto',
      name: <div className="fw-bolder text-muted me-5">Monto</div>,
      selector: (row: any) => Number(row.cobro),
      cell: (row: any) => (
        <div>
          <span className="text-gray-700 fw-bolder">
            {`${formatPrice(row.cobro, 'es-MX', 'MXN', 2, 2)}`}
          </span>
          <span className="text-gray-600 ms-3 fs-8">
            {row.unidadMoneda
              ? `/ ${row.unidadMoneda.code}`
              : 'Sin unidad de moneda'}
          </span>
        </div>
      ),
      sortable: true,
    },
    {
      id: 'fecha_solicitud',
      name: <div className="fw-bolder text-muted me-5">Fecha de solicitud</div>,
      selector: (row: any) =>
        new Date(row.created_at).toISOString().split('T')[0],
      cell: (row: any) => (
        <div className="text-gray-700">
          {new Date(row.created_at).toISOString().split('T')[0]}
        </div>
      ),
      sortable: true,
    },
    {
      id: 'fecha_facturacion',
      name: (
        <div className="fw-bolder text-muted me-5">Fecha de facturación</div>
      ),
      selector: (row: any) =>
        row.fecha_facturacion
          ? new Date(row.fecha_facturacion).toISOString().split('T')[0]
          : 'No registrado',
      cell: (row: any) => (
        <div className="text-gray-700">
          {row.fecha_facturacion
            ? new Date(row.fecha_facturacion).toISOString().split('T')[0]
            : 'No registrado'}
        </div>
      ),
      sortable: true,
    },
    {
      id: 'fecha_vencimiento',
      name: (
        <div className="fw-bolder text-muted me-5">Fecha de vencimiento</div>
      ),
      selector: (row: any) =>
        row.fecha_vencimiento
          ? new Date(row.fecha_vencimiento).toISOString().split('T')[0]
          : 'No registrado',
      cell: (row: any) => (
        <div className="text-gray-700">
          {row.fecha_vencimiento
            ? new Date(row.fecha_vencimiento).toISOString().split('T')[0]
            : 'No registrado'}
        </div>
      ),
      sortable: true,
    },
    {
      id: 'estatus',
      name: <div className="fw-bolder text-muted me-5">Estatus</div>,
      selector: (row: any) =>
        row.estatus ? row.estatus.name : 'No registrado',
      cell: (row: any) => (
        <div
          className={`${
            row.estatus ? colorStatusCuentasCobrar(row.estatus.code) : ''
          }`}
        >
          {row.estatus ? row.estatus.name : 'N/A'}{' '}
        </div>
      ),
      sortable: true,
      center: true,
    },
  ];

  return (
    <>
      <DataTable
        className="mb-5"
        columns={columns_section}
        data={d.data.items}
        customStyles={{
          table: {
            style: {
              width: '900px',
              minWidth: '100%',
            },
          },
        }}
        responsive
        noDataComponent={
          <>
            <div className="d-flex align-items-center ">
              <div className="row">
                <div className="col-xxl-12 col-xl-12 col-md-12 collg-12 text-center wh-0100">
                  <div className="text-muted fs-6 w-100 m-5">Sin registros</div>
                </div>
              </div>
            </div>
          </>
        }
      />
    </>
  );
};

export { ListadoTabla };
